import FeatureGates from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';
import {
	type EntityATI,
	type TProductDataRegions as ProductDataRegions,
	type TProductEdition as ProductEdition,
	searchDialogQuery,
	type SearchPageNode,
	type SearchPageQueryVariables,
	type ThirdPartyFilters,
} from '@atlassian/search-client';
import { getEdgeRegionHint } from '@atlassian/search-common';
import {
	type BackendExperiment,
	type ExperimentConfig,
	getBackendExperiment,
	type SearchExperimentLayer,
} from '@atlassian/search-experiment';

import {
	JIRA_BOARD_ENTITY_ATI,
	JIRA_DASHBOARD_ENTITY_ATI,
	JIRA_FILTER_ENTITY_ATI,
	JIRA_ISSUE_ENTITY_ATI,
	JIRA_PLAN_ENTITY_ATI,
	JIRA_PROJECT_ENTITY_ATI,
} from '../../../../common/constants/entities';
import { type PrimaryProductKey, ProductKeys3P } from '../../../../common/constants/products';
import { type ThirdPartyConfigsBootstrap } from '../../../../common/constants/schemas/3p-config';
import { type SelectedFiltersType } from '../../../../common/constants/types';
import { serveRegionSteeringExperiment } from '../../../../common/utils/experiments';
import { getEmptySelectedFilters, getThirdPartyFilters } from '../../../../common/utils/filters';
import { isHello } from '../../../../common/utils/is-hello';
import {
	getUserShardSpaceL1Id,
	serveUserShardControl,
	serveUserShardModel,
	serveUserShardUI,
} from '../../../store/bootstrap';
import type { UseTypeEntitiesResult } from '../../../store/search';

const experience = 'quick-find';

export const getThirdPartyFiltersForQuickFind = (thirdPartyConfigs: ThirdPartyConfigsBootstrap) => {
	const excluded3PProducts: ProductKeys3P[] = [
		ProductKeys3P.Slack,
		ProductKeys3P.Teams,
		ProductKeys3P.Gmail,
		ProductKeys3P.OutlookMail,
	];

	// Adding additional 3P products not included in thirdPartyConfigs
	const additional3PProducts: ProductKeys3P[] = [
		ProductKeys3P.Loom, // Loom is 1P product but treated as 3P in API filters
	];

	const thirdPartyKeys = Object.keys(thirdPartyConfigs) as ProductKeys3P[];

	const quickFind3PProducts: ProductKeys3P[] = additional3PProducts
		.concat(thirdPartyKeys)
		.filter((productKey) => !excluded3PProducts.includes(productKey));

	return getThirdPartyFilters(
		quickFind3PProducts,
		false, // titleMatchOnly value
		[], // it will search all subtypes if no entities are provided
		thirdPartyConfigs,
	);
};

// Experiment cohorts explained here: https://hello.atlassian.net/wiki/spaces/CentAI/pages/6112212590/Experiment+Milestones+for+Adding+3Ps+to+Search+Results
export const serve3PinQuickFindTest1 = (thirdPartyConfigs: ThirdPartyConfigsBootstrap) => {
	const isEligible3PUser = Object.keys(thirdPartyConfigs).some(
		(productKey) => thirdPartyConfigs[productKey]?.userNeedsOAuth === false,
	);

	return (
		isEligible3PUser &&
		FeatureGates.getExperimentValue<string>(
			'quick_find_3p_search_results_experiment',
			'cohort',
			'control',
		) === 'test-1'
	);
};

// Experiment cohorts explained here: https://hello.atlassian.net/wiki/spaces/CentAI/pages/6112212590/Experiment+Milestones+for+Adding+3Ps+to+Search+Results
export const serve3PinQuickFindTest2 = (thirdPartyConfigs: ThirdPartyConfigsBootstrap) => {
	const isEligible3PUser = Object.keys(thirdPartyConfigs).some(
		(productKey) => thirdPartyConfigs[productKey]?.userNeedsOAuth === false,
	);

	return (
		isEligible3PUser &&
		FeatureGates.getExperimentValue<string>(
			'quick_find_3p_search_results_experiment',
			'cohort',
			'control',
		) === 'test-2'
	);
};

export const fetchConfluenceAnd3PEntities = async (
	aggAbsoluteUrl: string | undefined,
	query: string,
	sourceProduct: PrimaryProductKey | undefined,
	cloudId: string,
	searchSessionId: string,
	selectedFilters: SelectedFiltersType | null,
	experimentConfig: ExperimentConfig,
	productEdition: ProductEdition | undefined,
	productDataRegions: ProductDataRegions | undefined,
	setRequestedBackendExperiments: (requestedBackendExperiments: BackendExperiment) => void,
	filteredEntities: string[],
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	sessionId?: string,
) => {
	// TODO: replace all the hard-coded values of filters with the ones passed on from selectedFilters
	const { commonFilters, confluenceFilters, mercuryFilters, compassFilters, assetsFilters } =
		selectedFilters || getEmptySelectedFilters();

	const isUserShardModel = serveUserShardModel(experimentConfig);
	const isUserShardUI = serveUserShardUI(experimentConfig);
	const isUserShardControl = serveUserShardControl(experimentConfig);
	const serve3pinQuickFindTest1 = serve3PinQuickFindTest1(thirdPartyConfigs);
	const serve3pinQuickFindTest2 = serve3PinQuickFindTest2(thirdPartyConfigs);

	const thirdPartyFilters: ThirdPartyFilters = serve3pinQuickFindTest1
		? getThirdPartyFiltersForQuickFind(thirdPartyConfigs)
		: { titleMatchOnly: true, subtypes: [] };

	const supportedEntities = [
		'ati:cloud:confluence:page',
		'ati:cloud:confluence:blogpost',
		'ati:cloud:confluence:attachment',
		'ati:cloud:confluence:whiteboard',
		'ati:cloud:confluence:database',
		'ati:cloud:confluence:embed',
		'ati:cloud:confluence:folder',
		...(isUserShardModel || serve3pinQuickFindTest1 || serve3pinQuickFindTest2
			? ['ati:cloud:graph:document']
			: []),
		...(fg('quickfind-smartlinks') || serve3pinQuickFindTest1 || serve3pinQuickFindTest2
			? ['ati:cloud:graph:remote-link']
			: []),
		...(serve3pinQuickFindTest1
			? [
					'ati:cloud:graph:branch',
					'ati:cloud:graph:calendar-event',
					'ati:cloud:graph:design',
					'ati:cloud:graph:pull-request',
					'ati:cloud:graph:repository',
					'ati:cloud:graph:video',
					'ati:cloud:graph:work-item',
					// These entities are excluded to reduce the noise of 3p results in QF (https://hello.atlassian.net/wiki/x/SDhKYgE)
					// 'ati:cloud:graph:conversation',
					// 'ati:cloud:graph:message',
					// 'ati:cloud:graph:commit',
				]
			: []),
	] as EntityATI[];

	const entities = supportedEntities.filter((supportedEntity) =>
		filteredEntities.includes(supportedEntity),
	);

	const backendExperiment = getBackendExperiment({
		experimentConfig,
		entities,
		productEdition,
		isHello: isHello(cloudId),
		productDataRegions,
		experience,
		tenantId: cloudId,
		thirdPartyFilters,
	});

	setRequestedBackendExperiments(backendExperiment);

	const { experimentLayers, experimentId, shadowExperimentId } = backendExperiment || {};

	const finalExperimentId = isUserShardModel ? 'L1-usershard' : experimentId;
	const finalExperimentLayers =
		isUserShardModel || isUserShardControl
			? [
					{
						name: 'search_platform_layer_l1',
						layerId: null,
						shadowId: null,
						definitions: [
							{
								layerId: isUserShardModel ? 'L1-usershard-shosexp' : 'default',
								entity: '*',
								subEntity: '*',
								shadowId: null,
								abTestId: 'quick-find-user-shard-search-experiment',
							},
						],
						analyticsDefinitions: [
							{
								layerId: isUserShardModel ? 'L1-usershard-shosexp' : 'default',
								entity: '*',
								subEntity: '*',
								shadowId: null,
								abTestId: 'quick-find-user-shard-search-experiment',
								controlId: 'default',
							},
						],
					},
					{
						name: 'search_platform_layer_l2',
						layerId: null,
						shadowId: null,
						definitions: [
							{
								layerId: isUserShardModel ? 'L2-pipelineOnly' : 'default',
								entity: '*',
								subEntity: '*',
								shadowId: null,
								abTestId: 'quick-find-user-shard-search-experiment',
							},
						],
						analyticsDefinitions: [
							{
								layerId: isUserShardModel ? 'L2-pipelineOnly' : 'default',
								entity: '*',
								subEntity: '*',
								shadowId: null,
								abTestId: 'quick-find-user-shard-search-experiment',
								controlId: 'default',
							},
						],
					},
				]
			: experimentLayers;

	// Default behaviour currently is to include the confluenceEntity hydration,
	// hence the control is to include it. But we check that the cohort is explicitly
	// 'test' to ensure that this user is actually in the experiment.
	const excludeConfluenceEntity =
		FeatureGates.getExperimentValue<string>(
			'remove_confluence_entity_hydration_experiment',
			'cohort',
			'control',
		) === 'test';

	const limit = isUserShardUI ? 10 : 7;

	const variables = {
		experience,
		query,
		queryVersion: 1,
		cloudIdARI: `ari:cloud:confluence::site/${cloudId}`,
		tenantId: cloudId,
		limit,
		commonFilters,
		confluenceFilters,
		assetsFilters,
		jiraFilters: {
			issueFilter: {
				projectARIs: [],
				assigneeARIs: [],
				reporterARIs: [],
				commenterARIs: [],
				issueTypeIDs: [],
				issueLabels: [],
				issueTypes: [],
				statusCategories: [],
				watcherARIs: [],
			},
		},
		mercuryFilters,
		compassFilters,
		thirdPartyFilters,
		entities,
		sort: [],
		searchSessionId,
		sessionId,
		sourceProduct,
		experimentId: finalExperimentId,
		shadowExperimentId,
		experimentLayers: mapExperimentLayers(finalExperimentLayers),
		includeSocialSignals: false,
		includeUserSignals: isUserShardUI,
		includeSpaceSubtype: false,
		includeConfluenceEntity: !excludeConfluenceEntity,
		skipFieldsFor3pInQuickFind: serve3pinQuickFindTest1 || serve3pinQuickFindTest2,
	} satisfies SearchPageQueryVariables;

	const headers =
		serveRegionSteeringExperiment() && sourceProduct && productDataRegions
			? {
					'atl-edge-region-hint': getEdgeRegionHint(sourceProduct, productDataRegions),
				}
			: undefined;

	const response = await searchDialogQuery({
		variables,
		aggAbsoluteUrl,
		serveUserShardUI: isUserShardUI,
		headers,
	});
	const nodes = response.data?.search?.results.edges.map((edge) => edge.node) || [];
	return {
		fetchResults: nodes.map(mapEssentialData),
		fetchEntitiesRequestId: response.extensions?.gateway?.request_id,
		fetchEntitiesTraceId: response.extensions?.gateway?.trace_id,
		errors: response.errors || [],
	};
};

export const fetchConfluenceSpaces = async (
	aggAbsoluteUrl: string | undefined,
	query: string,
	sourceProduct: PrimaryProductKey | undefined,
	cloudId: string,
	searchSessionId: string,
	selectedFilters: SelectedFiltersType | null,
	experimentConfig: ExperimentConfig,
	productEdition: ProductEdition | undefined,
	productDataRegions: ProductDataRegions | undefined,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	sessionId?: string,
) => {
	// TODO: replace all the hard-coded values of filters with the ones passed on from selectedFilters
	// and remove experiementId and shadowExperimentId as these are no longer used
	const { commonFilters, confluenceFilters, mercuryFilters, compassFilters, assetsFilters } =
		selectedFilters || getEmptySelectedFilters();

	const entities: EntityATI[] = ['ati:cloud:confluence:space'];

	const backendExperiment = getBackendExperiment({
		experimentConfig,
		entities,
		productEdition,
		isHello: isHello(cloudId),
		productDataRegions,
		experience,
		tenantId: cloudId,
	});

	const { experimentLayers, experimentId, shadowExperimentId } = backendExperiment || {};

	const isUserShardModel = serveUserShardModel(experimentConfig);
	const isUserShardControl = serveUserShardControl(experimentConfig);
	const userShardSpaceL1Id = getUserShardSpaceL1Id(experimentConfig);
	const userShardLayerId = isUserShardModel ? (userShardSpaceL1Id ?? 'default') : 'default';

	const finalExperimentId = isUserShardModel ? 'L1-usershard' : experimentId;
	const finalExperimentLayers =
		isUserShardModel || isUserShardControl
			? [
					{
						name: 'search_platform_layer_l1',
						layerId: null,
						shadowId: null,
						definitions: [
							{
								layerId: userShardLayerId,
								entity: '*',
								subEntity: '*',
								shadowId: null,
								abTestId: 'quick-find-user-shard-search-experiment',
							},
						],
						analyticsDefinitions: [
							{
								layerId: userShardLayerId,
								entity: '*',
								subEntity: '*',
								shadowId: null,
								abTestId: 'quick-find-user-shard-search-experiment',
								controlId: 'default',
							},
						],
					},
					{
						name: 'search_platform_layer_l2',
						layerId: null,
						shadowId: null,
						definitions: [
							{
								layerId: isUserShardModel ? 'L2-pipelineOnly' : 'default',
								entity: '*',
								subEntity: '*',
								shadowId: null,
								abTestId: 'quick-find-user-shard-search-experiment',
							},
						],
						analyticsDefinitions: [
							{
								layerId: isUserShardModel ? 'L2-pipelineOnly' : 'default',
								entity: '*',
								subEntity: '*',
								shadowId: null,
								abTestId: 'quick-find-user-shard-search-experiment',
								controlId: 'default',
							},
						],
					},
				]
			: experimentLayers;

	const variables = {
		experience,
		query,
		queryVersion: 1,
		cloudIdARI: `ari:cloud:confluence::site/${cloudId}`,
		tenantId: cloudId,
		limit: 1,
		commonFilters,
		confluenceFilters,
		assetsFilters,
		jiraFilters: {
			issueFilter: {
				projectARIs: [],
				assigneeARIs: [],
				reporterARIs: [],
				commenterARIs: [],
				issueTypeIDs: [],
				issueLabels: [],
				issueTypes: [],
				statusCategories: [],
				watcherARIs: [],
			},
		},
		mercuryFilters,
		compassFilters,
		thirdPartyFilters: {
			titleMatchOnly: false,
			subtypes: [],
		},
		entities,
		sort: [],
		searchSessionId,
		sessionId,
		sourceProduct,
		experimentId: finalExperimentId,
		shadowExperimentId,
		experimentLayers: mapExperimentLayers(finalExperimentLayers),
		includeSocialSignals: false,
		includeSpaceSubtype: false,
	} satisfies SearchPageQueryVariables;

	const headers =
		serveRegionSteeringExperiment() && sourceProduct && productDataRegions
			? {
					'atl-edge-region-hint': getEdgeRegionHint(sourceProduct, productDataRegions),
				}
			: undefined;

	const response = await searchDialogQuery({
		variables,
		aggAbsoluteUrl,
		serveUserShardUI: false, // Spaces don't have user action data
		headers,
	});
	const nodes = response.data?.search?.results.edges.map((edge) => edge.node) || [];
	return {
		fetchResults: nodes.map(mapEssentialData),
		fetchSpacesRequestId: response.extensions?.gateway?.request_id,
		errors: response.errors || [],
	};
};

export const fetchAtlasEntities = async (
	aggAbsoluteUrl: string | undefined,
	query: string,
	sourceProduct: PrimaryProductKey | undefined,
	cloudId: string,
	searchSessionId: string,
	selectedFilters: SelectedFiltersType | null,
	experimentConfig: ExperimentConfig,
	productEdition: ProductEdition | undefined,
	productDataRegions: ProductDataRegions | undefined,
	setRequestedBackendExperiments: (requestedBackendExperiments: BackendExperiment) => void,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	sessionId?: string,
) => {
	// TODO: replace all the hard-coded values of filters with the ones passed on from selectedFilters
	// and remove experiementId and shadowExperimentId as these are no longer used
	const { commonFilters, mercuryFilters, compassFilters, assetsFilters } =
		selectedFilters || getEmptySelectedFilters();

	const serve3pinQuickFindTest1 = serve3PinQuickFindTest1(thirdPartyConfigs);
	const serve3pinQuickFindTest2 = serve3PinQuickFindTest2(thirdPartyConfigs);
	const thirdPartyFilters: ThirdPartyFilters = serve3pinQuickFindTest1
		? getThirdPartyFiltersForQuickFind(thirdPartyConfigs)
		: { titleMatchOnly: false, subtypes: [] };

	const entities: EntityATI[] = [
		'ati:cloud:townsquare:project',
		'ati:cloud:townsquare:goal',
		'ati:cloud:townsquare:tag',
		...(serve3pinQuickFindTest1 || serve3pinQuickFindTest2
			? (['ati:cloud:graph:document', 'ati:cloud:graph:remote-link'] as EntityATI[])
			: []),
		...(serve3pinQuickFindTest1
			? ([
					'ati:cloud:graph:branch',
					'ati:cloud:graph:calendar-event',
					'ati:cloud:graph:design',
					'ati:cloud:graph:pull-request',
					'ati:cloud:graph:repository',
					'ati:cloud:graph:video',
					'ati:cloud:graph:work-item',
					// These entities are excluded to reduce the noise of 3p results in QF (https://hello.atlassian.net/wiki/x/SDhKYgE)
					// 'ati:cloud:graph:conversation',
					// 'ati:cloud:graph:message',
					// 'ati:cloud:graph:commit',
				] as EntityATI[])
			: []),
	];

	const backendExperiment = getBackendExperiment({
		experimentConfig,
		entities,
		productEdition,
		isHello: isHello(cloudId),
		productDataRegions,
		experience,
		tenantId: cloudId,
	});

	setRequestedBackendExperiments(backendExperiment);

	const { experimentLayers, experimentId, shadowExperimentId } = backendExperiment || {};

	const variables = {
		experience,
		query,
		queryVersion: 1,
		cloudIdARI: `ari:cloud:confluence::site/${cloudId}`,
		tenantId: cloudId,
		limit: 6,
		commonFilters,
		assetsFilters,
		confluenceFilters: {
			spacesFilter: [],
			contributorsFilter: [],
			ancestorIdsFilter: [],
			range: [],
			owners: [],
			labelsFilter: [],
			titleMatchOnly: true,
			contentStatuses: [],
			containerStatus: [],
		},
		jiraFilters: {
			issueFilter: {
				projectARIs: [],
				assigneeARIs: [],
				reporterARIs: [],
				commenterARIs: [],
				issueTypeIDs: [],
				issueLabels: [],
				issueTypes: [],
				statusCategories: [],
				watcherARIs: [],
			},
		},
		mercuryFilters,
		compassFilters,
		thirdPartyFilters,
		entities,
		sort: [],
		searchSessionId,
		sessionId,
		sourceProduct,
		experimentId,
		shadowExperimentId,
		experimentLayers: mapExperimentLayers(experimentLayers),
		includeSocialSignals: false,
		includeSpaceSubtype: false,
		skipFieldsFor3pInQuickFind: serve3pinQuickFindTest1 || serve3pinQuickFindTest2,
	} satisfies SearchPageQueryVariables;

	const headers =
		serveRegionSteeringExperiment() && sourceProduct && productDataRegions
			? {
					'atl-edge-region-hint': getEdgeRegionHint(sourceProduct, productDataRegions),
				}
			: undefined;

	const response = await searchDialogQuery({
		variables,
		aggAbsoluteUrl,
		serveUserShardUI: false, // Atlas entities don't have user action data
		headers,
	});

	const nodes = response.data?.search?.results.edges.map((edge) => edge.node) || [];
	return {
		fetchResults: nodes.map(mapEssentialData),
		fetchEntitiesRequestId: response.extensions?.gateway?.request_id,
		fetchEntitiesTraceId: response.extensions?.gateway?.trace_id,
		errors: response.errors || [],
	};
};

export const fetchMercuryEntities = async (
	aggAbsoluteUrl: string | undefined,
	query: string,
	sourceProduct: PrimaryProductKey | undefined,
	cloudId: string,
	searchSessionId: string,
	selectedFilters: SelectedFiltersType | null,
	experimentConfig: ExperimentConfig,
	productEdition: ProductEdition | undefined,
	productDataRegions: ProductDataRegions | undefined,
	setRequestedBackendExperiments: (requestedBackendExperiments: BackendExperiment) => void,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	sessionId?: string,
) => {
	// TODO: replace all the hard-coded values of filters with the ones passed on from selectedFilters
	// and remove experiementId and shadowExperimentId as these are no longer used
	const {
		commonFilters,
		confluenceFilters,
		jiraFilters,
		mercuryFilters,
		compassFilters,
		assetsFilters,
		thirdPartyFilters,
	} = selectedFilters || getEmptySelectedFilters();

	const entities: EntityATI[] = [
		'ati:cloud:mercury:focus-area',
		'ati:cloud:mercury:focus-area-status-update',
	];

	const backendExperiment = getBackendExperiment({
		experimentConfig,
		entities,
		productEdition,
		isHello: isHello(cloudId),
		productDataRegions,
		experience,
		tenantId: cloudId,
	});

	setRequestedBackendExperiments(backendExperiment);

	const { experimentLayers, experimentId, shadowExperimentId } = backendExperiment || {};

	const variables = {
		experience,
		query,
		queryVersion: 1,
		cloudIdARI: `ari:cloud:confluence::site/${cloudId}`,
		tenantId: cloudId,
		limit: 6,
		commonFilters: commonFilters,
		confluenceFilters,
		assetsFilters,
		jiraFilters,
		mercuryFilters,
		compassFilters,
		thirdPartyFilters,
		entities,
		sort: [],
		searchSessionId,
		sessionId,
		sourceProduct,
		experimentId,
		shadowExperimentId,
		experimentLayers: mapExperimentLayers(experimentLayers),
		includeSocialSignals: false,
		includeSpaceSubtype: false,
	} satisfies SearchPageQueryVariables;

	const headers =
		serveRegionSteeringExperiment() && sourceProduct && productDataRegions
			? {
					'atl-edge-region-hint': getEdgeRegionHint(sourceProduct, productDataRegions),
				}
			: undefined;

	const response = await searchDialogQuery({
		variables,
		aggAbsoluteUrl,
		serveUserShardUI: false, // Mercury entities don't have user action data
		headers,
	});

	const nodes = response.data?.search?.results.edges.map((edge) => edge.node) || [];
	return {
		fetchResults: nodes.map(mapEssentialData),
		fetchEntitiesRequestId: response.extensions?.gateway?.request_id,
		fetchEntitiesTraceId: response.extensions?.gateway?.trace_id,
		errors: response.errors || [],
	};
};

export const fetchTalentEntities = async (
	aggAbsoluteUrl: string | undefined,
	query: string,
	sourceProduct: PrimaryProductKey | undefined,
	cloudId: string,
	searchSessionId: string,
	selectedFilters: SelectedFiltersType | null,
	experimentConfig: ExperimentConfig,
	productEdition: ProductEdition | undefined,
	productDataRegions: ProductDataRegions | undefined,
	setRequestedBackendExperiments: (requestedBackendExperiments: BackendExperiment) => void,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	sessionId?: string,
) => {
	// TODO: replace all the hard-coded values of filters with the ones passed on from selectedFilters
	// and remove experiementId and shadowExperimentId as these are no longer used
	const {
		commonFilters,
		confluenceFilters,
		jiraFilters,
		mercuryFilters,
		compassFilters,
		assetsFilters,
		thirdPartyFilters,
		talentFilters,
	} = selectedFilters || getEmptySelectedFilters();

	const entities: EntityATI[] = ['ati:cloud:radar:position'];

	const backendExperiment = getBackendExperiment({
		experimentConfig,
		entities,
		productEdition,
		isHello: isHello(cloudId),
		productDataRegions,
		experience,
		tenantId: cloudId,
	});

	setRequestedBackendExperiments(backendExperiment);

	const { experimentLayers, experimentId, shadowExperimentId } = backendExperiment || {};

	const variables = {
		experience,
		query,
		queryVersion: 1,
		cloudIdARI: `ari:cloud:confluence::site/${cloudId}`,
		tenantId: cloudId,
		limit: 6,
		commonFilters: commonFilters,
		confluenceFilters,
		assetsFilters,
		jiraFilters,
		mercuryFilters,
		compassFilters,
		thirdPartyFilters,
		talentFilters,
		entities,
		sort: [],
		searchSessionId,
		sessionId,
		sourceProduct,
		experimentId,
		shadowExperimentId,
		experimentLayers: mapExperimentLayers(experimentLayers),
		includeSocialSignals: false,
		includeSpaceSubtype: false,
	} satisfies SearchPageQueryVariables;

	const headers =
		serveRegionSteeringExperiment() && sourceProduct && productDataRegions
			? {
					'atl-edge-region-hint': getEdgeRegionHint(sourceProduct, productDataRegions),
				}
			: undefined;

	const response = await searchDialogQuery({
		variables,
		aggAbsoluteUrl,
		serveUserShardUI: false, // Mercury entities don't have user action data
		headers,
	});

	const nodes = response.data?.search?.results.edges.map((edge) => edge.node) || [];
	return {
		fetchResults: nodes.map(mapEssentialData),
		fetchEntitiesRequestId: response.extensions?.gateway?.request_id,
		fetchEntitiesTraceId: response.extensions?.gateway?.trace_id,
		errors: response.errors || [],
	};
};

export const fetchAssetsEntities = async (
	aggAbsoluteUrl: string | undefined,
	query: string,
	sourceProduct: PrimaryProductKey | undefined,
	cloudId: string,
	searchSessionId: string,
	selectedFilters: SelectedFiltersType | null,
	experimentConfig: ExperimentConfig,
	productEdition: ProductEdition | undefined,
	productDataRegions: ProductDataRegions | undefined,
	setRequestedBackendExperiments: (requestedBackendExperiments: BackendExperiment) => void,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	sessionId?: string,
) => {
	// TODO: replace all the hard-coded values of filters with the ones passed on from selectedFilters
	// and remove experiementId and shadowExperimentId as these are no longer used
	const {
		commonFilters,
		confluenceFilters,
		jiraFilters,
		mercuryFilters,
		compassFilters,
		assetsFilters,
		thirdPartyFilters,
	} = selectedFilters || getEmptySelectedFilters();

	const entities: EntityATI[] = [
		'ati:cloud:cmdb:object',
		'ati:cloud:cmdb:schema',
		'ati:cloud:cmdb:type',
	];

	const backendExperiment = getBackendExperiment({
		experimentConfig,
		entities,
		productEdition,
		isHello: isHello(cloudId),
		productDataRegions,
		experience,
		tenantId: cloudId,
	});

	setRequestedBackendExperiments(backendExperiment);

	const { experimentLayers, experimentId, shadowExperimentId } = backendExperiment || {};

	const variables = {
		experience,
		query,
		queryVersion: 1,
		cloudIdARI: `ari:cloud:confluence::site/${cloudId}`,
		tenantId: cloudId,
		limit: 6,
		commonFilters: commonFilters,
		confluenceFilters,
		assetsFilters,
		jiraFilters,
		mercuryFilters,
		compassFilters,
		thirdPartyFilters,
		entities,
		sort: [],
		searchSessionId,
		sessionId,
		sourceProduct,
		experimentId,
		shadowExperimentId,
		experimentLayers: mapExperimentLayers(experimentLayers),
		includeSocialSignals: false,
		includeSpaceSubtype: false,
	} satisfies SearchPageQueryVariables;

	const headers =
		serveRegionSteeringExperiment() && sourceProduct && productDataRegions
			? {
					'atl-edge-region-hint': getEdgeRegionHint(sourceProduct, productDataRegions),
				}
			: undefined;

	const response = await searchDialogQuery({
		variables,
		aggAbsoluteUrl,
		headers,
	});

	const nodes = response.data?.search?.results.edges.map((edge) => edge.node) || [];
	return {
		fetchResults: nodes.map(mapEssentialData),
		fetchEntitiesRequestId: response.extensions?.gateway?.request_id,
		fetchEntitiesTraceId: response.extensions?.gateway?.trace_id,
		errors: response.errors || [],
	};
};

/**
 * The experiment layers are mapped to ensure permitted attributes are sent into search requests.
 */
const mapExperimentLayers = (experimentLayers?: SearchExperimentLayer[]) =>
	experimentLayers?.map(({ name, layerId, shadowId, definitions }) => ({
		name,
		layerId,
		shadowId,
		definitions,
	}));

export const fetchJiraIssuesAndTypeEntities = async (
	aggAbsoluteUrl: string | undefined,
	query: string,
	sourceProduct: PrimaryProductKey | undefined,
	cloudId: string,
	searchSessionId: string,
	selectedFilters: SelectedFiltersType | null,
	experimentConfig: ExperimentConfig,
	productEdition: ProductEdition | undefined,
	productDataRegions: ProductDataRegions | undefined,
	setRequestedBackendExperiments: (requestedBackendExperiments: BackendExperiment) => void,
	isJiraIssueIndexed: boolean | undefined,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	typeFilterEntities: UseTypeEntitiesResult,
	sessionId?: string,
	hasEditOrViewPlanPermission?: boolean,
) => {
	const {
		commonFilters,
		confluenceFilters,
		mercuryFilters,
		compassFilters,
		thirdPartyFilters,
		jiraFilters,
		assetsFilters,
	} = selectedFilters || getEmptySelectedFilters();
	const { selectedEntities, entitiesForQuery } = typeFilterEntities;

	const supportedEntities: EntityATI[] = [
		JIRA_ISSUE_ENTITY_ATI,
		JIRA_PROJECT_ENTITY_ATI,
		JIRA_BOARD_ENTITY_ATI,
		JIRA_FILTER_ENTITY_ATI,
		JIRA_DASHBOARD_ENTITY_ATI,
	];
	if (hasEditOrViewPlanPermission || !fg('rovo_search_check_plan_permission')) {
		supportedEntities.push(JIRA_PLAN_ENTITY_ATI);
	}

	const entities: EntityATI[] = selectedEntities.length
		? supportedEntities.filter((supportedEntity) => entitiesForQuery.includes(supportedEntity))
		: [JIRA_ISSUE_ENTITY_ATI];

	const backendExperiment = getBackendExperiment({
		experimentConfig,
		entities,
		productEdition,
		isHello: isHello(cloudId),
		productDataRegions,
		experience,
		tenantId: cloudId,
		isJiraIssueIndexed,
	});

	setRequestedBackendExperiments(backendExperiment);

	const { experimentLayers } = backendExperiment || {};

	const variables = {
		experience,
		query,
		queryVersion: 1,
		cloudIdARI: `ari:cloud:jira::site/${cloudId}`,
		tenantId: cloudId,
		limit: fg('search_qf_increase_issues_in_jira') ? 12 : 7,
		commonFilters,
		confluenceFilters,
		jiraFilters,
		mercuryFilters,
		compassFilters,
		thirdPartyFilters,
		entities,
		sort: [],
		searchSessionId,
		sessionId,
		sourceProduct,
		experimentLayers: mapExperimentLayers(experimentLayers),
		includeSocialSignals: false,
		includeJiraIssueType: true,
		includeSpaceSubtype: false,
		includeIssueTypeAvatarUrl: fg('rovo_search_jira_icons'),
		assetsFilters,
	} satisfies SearchPageQueryVariables;

	const headers =
		serveRegionSteeringExperiment() && sourceProduct && productDataRegions
			? {
					'atl-edge-region-hint': getEdgeRegionHint(sourceProduct, productDataRegions),
				}
			: undefined;

	const response = await searchDialogQuery({
		variables,
		aggAbsoluteUrl,
		serveUserShardUI: false, // Jira entities don't have user action data
		headers,
	});
	const nodes = response.data?.search?.results.edges.map((edge) => edge.node) || [];
	return {
		fetchResults: nodes.map(mapEssentialData),
		fetchEntitiesRequestId: response.extensions?.gateway?.request_id,
		fetchEntitiesTraceId: response.extensions?.gateway?.trace_id,
		errors: response.errors || [],
	};
};

export const fetchJiraEntities = async (
	aggAbsoluteUrl: string | undefined,
	query: string,
	sourceProduct: PrimaryProductKey | undefined,
	cloudId: string,
	searchSessionId: string,
	selectedFilters: SelectedFiltersType | null,
	experimentConfig: ExperimentConfig,
	productEdition: ProductEdition | undefined,
	productDataRegions: ProductDataRegions | undefined,
	setRequestedBackendExperiments: (requestedBackendExperiments: BackendExperiment) => void,
	isJiraIssueIndexed: boolean | undefined,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	sessionId?: string,
	hasEditOrViewPlanPermission?: boolean,
) => {
	const {
		commonFilters,
		confluenceFilters,
		mercuryFilters,
		compassFilters,
		thirdPartyFilters,
		jiraFilters,
		assetsFilters,
	} = selectedFilters || getEmptySelectedFilters();

	const entities: EntityATI[] = [
		JIRA_BOARD_ENTITY_ATI,
		JIRA_FILTER_ENTITY_ATI,
		JIRA_PROJECT_ENTITY_ATI,
		JIRA_DASHBOARD_ENTITY_ATI,
	];

	if (hasEditOrViewPlanPermission || !fg('rovo_search_check_plan_permission')) {
		entities.push(JIRA_PLAN_ENTITY_ATI);
	}
	const backendExperiment = getBackendExperiment({
		experimentConfig,
		entities,
		productEdition,
		isHello: isHello(cloudId),
		productDataRegions,
		experience,
		tenantId: cloudId,
		isJiraIssueIndexed,
	});

	setRequestedBackendExperiments(backendExperiment);

	const { experimentLayers } = backendExperiment || {};

	const variables = {
		experience,
		query,
		queryVersion: 1,
		cloudIdARI: `ari:cloud:jira::site/${cloudId}`,
		tenantId: cloudId,
		limit: 5,
		commonFilters,
		confluenceFilters,
		jiraFilters,
		mercuryFilters,
		compassFilters,
		thirdPartyFilters,
		assetsFilters,
		entities,
		sort: [],
		searchSessionId,
		sessionId,
		sourceProduct,
		experimentLayers: mapExperimentLayers(experimentLayers),
		includeSocialSignals: false,
		includeJiraBoardContainer: true,
		includeSpaceSubtype: false,
		includeBoardObject: fg('rovo_search_update_board_url'),
	} satisfies SearchPageQueryVariables;

	const headers =
		serveRegionSteeringExperiment() && sourceProduct && productDataRegions
			? {
					'atl-edge-region-hint': getEdgeRegionHint(sourceProduct, productDataRegions),
				}
			: undefined;

	const response = await searchDialogQuery({
		variables,
		aggAbsoluteUrl,
		headers,
	});
	const nodes = response.data?.search?.results.edges.map((edge) => edge.node) || [];
	return {
		fetchResults: nodes.map(mapEssentialData),
		errors: response.errors || [],
	};
};

export const mapEssentialData = (result: SearchPageNode) => ({
	id: result.id,
	title: result.title,
	data: result,
	type: 'search-result' as const,
});
